import { type FeatureFlag, type FeatureFlagList, type FlagState } from '@kalos/kalos-rpc';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FeatureFlagClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

/**
 * Feature flag state from kalos-core's FeatureFlagService.
 *
 * ListFlags has no permission check and answers the same for every caller:
 * every row (retired ones included) with its owners, plus the backend
 * registry. FeatureFlagProvider reads it for every signed-in user; the admin
 * page shares the same cache entry.
 *
 * The writes are the admin page's. Each needs FeatureFlagAdmin or ownership of
 * the flag (SetFlagOwners: FeatureFlagAdmin only), and outside prod every one
 * of them is refused with FAILED_PRECONDITION, because every flag is on there.
 * Each answers with the flag's row after the change, so the cached list is
 * patched rather than refetched.
 */

/** How often flag state is re-read. A flip reaches each user within this, at their next mount. */
export const FEATURE_FLAGS_REFRESH_MS = 5 * 60_000;

export const featureFlagsQueryKey = () => [queryKeys.featureFlags.root];

export const useFeatureFlagsQuery = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: featureFlagsQueryKey(),
    queryFn: async () => await FeatureFlagClientService.ListFlags(),
    enabled,
    staleTime: FEATURE_FLAGS_REFRESH_MS,
    refetchInterval: FEATURE_FLAGS_REFRESH_MS,
    retry: 1,
    // A failed read leaves every flag off, which is the safe answer; it is not worth a toast
    // on every page load (for instance while prod's backend predates FeatureFlagService).
    meta: { silentError: true },
  });

export const featureFlagChangesQueryKey = (key: string) => [queryKeys.featureFlags.changes, key];

/** A flag's change log, newest first. Owners and FeatureFlagAdmin only. */
export const useFeatureFlagChangesQuery = ({
  flagKey,
  enabled = true,
}: {
  flagKey: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: featureFlagChangesQueryKey(flagKey),
    queryFn: async () => await FeatureFlagClientService.ListFlagChanges(flagKey),
    enabled: enabled && !!flagKey,
    // The history drawer says inline why it could not load.
    meta: { silentError: true },
  });

/** Puts a write's answer into the cached list and drops that flag's cached history. */
const applyFlagWrite = async (queryClient: QueryClient, row: FeatureFlag) => {
  queryClient.setQueryData<FeatureFlagList>(featureFlagsQueryKey(), (list) => {
    if (!list) return list;
    const known = list.results.some((r) => r.key === row.key);
    const results = known
      ? list.results.map((r) => (r.key === row.key ? row : r))
      : [...list.results, row].sort((a, b) => a.key.localeCompare(b.key));
    return { ...list, results, totalCount: results.length };
  });
  await queryClient.invalidateQueries({ queryKey: featureFlagChangesQueryKey(row.key) });
};

export const useSetFlagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, state }: { key: string; state: FlagState }) =>
      await FeatureFlagClientService.SetFlag(key, state),
    onSuccess: (row) => applyFlagWrite(queryClient, row),
  });
};

export const useKeepFlagMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      keepUntil,
      keepReason,
    }: {
      key: string;
      keepUntil: string;
      keepReason: string;
    }) => await FeatureFlagClientService.KeepFlag(key, keepUntil, keepReason),
    onSuccess: (row) => applyFlagWrite(queryClient, row),
  });
};

export const useSetFlagOwnersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, ownerUserIds }: { key: string; ownerUserIds: number[] }) =>
      await FeatureFlagClientService.SetFlagOwners(key, ownerUserIds),
    onSuccess: (row) => applyFlagWrite(queryClient, row),
  });
};
